/**
 * EditTagModal Component
 * 
 * Modal for editing existing tags.
 * 
 * Features:
 * - Modal overlay with focus trap
 * - Tag form integration
 * - Keyboard navigation support
 * - WCAG 2.2 AA compliance
 * 
 * @see .specify/memory/constitution.md
 * @see specs/003-categorisation-feature/spec.md
 */

import React, { useEffect, useRef } from 'react';
import { TagForm } from './TagForm';
import { UpdateTagInput, Tag } from '../../../../types/tag.types';
import { X } from 'lucide-react';

interface EditTagModalProps {
  tag: Tag;
  onClose: () => void;
  onSubmit: (data: UpdateTagInput) => void;
  loading?: boolean;
}

export function EditTagModal({ 
  tag,
  onClose, 
  onSubmit,
  loading = false
}: EditTagModalProps): React.ReactElement {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  // Focus trap and keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Focus the first focusable element
    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-tag-title"
      aria-describedby="edit-tag-description"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleBackdropClick}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div>
              <h2 id="edit-tag-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Edit Tag
              </h2>
              <p id="edit-tag-description" className="text-sm text-gray-600 dark:text-gray-400">
                Update tag details
              </p>
            </div>
            <button
              ref={firstFocusableRef}
              onClick={onClose}
              className="btn-ghost btn-sm rounded-md p-2"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <TagForm
              tag={tag}
              onSubmit={onSubmit}
              onCancel={onClose}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

